"use client";
import React, { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
// Drawer modal removed - using fixed positioning drawer instead
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";
import { Label } from "../../../components/ui/label";
import axios from "axios";
import toast from "react-hot-toast";
import { CreateOrderRequest, Order, ProductItem } from "../types/order";
import { cn } from "../../../lib/utils";
// @ts-ignore
import { API_BASE_URL } from "../../../../src/App.js";
import { useSelector } from "react-redux";
import { ProductForm } from "./ProductForm.tsx";
import { CustomerForm } from "./CustomerForm.tsx";
import { SignatoryPicker } from "./SignatoryPicker.tsx";
import { DEFAULT_FREIGHT_NOTES, DEFAULT_WARRANTY, formatFreightChargesLineLabel } from "./quotationPdfShared";
import {
  getOrderRevisionBumpIntent,
  setOrderRevisionBumpIntent,
} from "../../../utils/orderRevisionIntent";
interface Customer {
  id: number;
  companyName: string;
  companyMobile: string;
  personName: string;
  personMobile: string;
  address: string;
  state: string;
  pincode?: string | null;
  gst?: string | null;
}

interface QuotationSettings {
  quotationSeries: string;
  numberSeries: number;
}

interface QuotationSignatory {
  id: number;
  name: string;
  mobile: string;
}

/** Default GST % on freight for new quotations (user can change in the form). */
const DEFAULT_FREIGHT_CHARGES_GST = "18";

interface Props {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
  userRole: "admin" | "dealer" | "employee";
  userId: string;
  existingOrder?: Order | null;
}

export const OrderForm: React.FC<Props> = ({
  open,
  onClose,
  refresh,
  userRole,
  userId,
  existingOrder,
}) => {
  const DEFAULT_TERMS_CONDITIONS = `✓ Work completion within agreed timeline
✓ 50% advance, balance on completion
✓ Quality materials guaranteed
✓ Professional workmanship`;

  const [form, setForm] = useState<Partial<CreateOrderRequest>>({
    status: "new",
    created_by: userId,
    products: [],
    freightChargesGst: DEFAULT_FREIGHT_CHARGES_GST,
    freightNotes: DEFAULT_FREIGHT_NOTES,
    warranty: DEFAULT_WARRANTY,
  });
  
  const [productModalOpen, setProductModalOpen] = useState(false);
    const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<ProductItem[]>([]);
  const { authToken = "", id = "" ,role="",username=""} = useSelector(
    (state: any) => state.loginData || {}
  );
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null);
  const [errors, setErrors] = useState<{ customer?: string; products?: string }>({});
  const [quotationSettings, setQuotationSettings] = useState<QuotationSettings>({ quotationSeries: "QT", numberSeries: 1 });
  const [signatories, setSignatories] = useState<QuotationSignatory[]>([]);
  const [bumpRevision, setBumpRevision] = useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!open || !id || existingOrder) return;
      try {
        const response = await axios.get(`${API_BASE_URL}account/${id}/settings`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setQuotationSettings({
          quotationSeries: response.data.quotationSeries || "QT",
          numberSeries: Number(response.data.numberSeries || 1),
        });
      } catch (error) {
        console.error("Failed to load quotation settings", error);
      }
    };

    fetchSettings();
  }, [open, id, authToken, existingOrder]);

  useEffect(() => {
    if (!open || !id || !authToken) return;
    axios
      .get(`${API_BASE_URL}account/${id}/quotation-signatories`, {
        params: { firstResults: 0, maxResults: 1000 },
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => setSignatories(response.data.signatories || response.data.data || []))
      .catch(() => setSignatories([]));
  }, [open, id, authToken]);

    useEffect(() => {
    if (existingOrder) {
      setBumpRevision(getOrderRevisionBumpIntent(existingOrder.id));
      const cr = existingOrder.customerRole;
      setForm({
        id: existingOrder.id,
        status: existingOrder.status,
        customerId: existingOrder.customerId,
        customerName: existingOrder.customerName,
        price: existingOrder.price,
        gst: existingOrder.gst,
        total: existingOrder.total,
        created_by: existingOrder.createdBy,
        deliveryDate: existingOrder.deliveryDate,
        subject: existingOrder.subject,
        validity: existingOrder.validity,
        freightCharges: existingOrder.freightCharges,
        freightChargesGst:
          existingOrder.freightChargesGst != null &&
          String(existingOrder.freightChargesGst).trim() !== ""
            ? String(existingOrder.freightChargesGst).trim()
            : DEFAULT_FREIGHT_CHARGES_GST,
        freightNotes:
          existingOrder.freightNotes != null && String(existingOrder.freightNotes).trim() !== ""
            ? String(existingOrder.freightNotes).trim()
            : DEFAULT_FREIGHT_NOTES,
        terms: existingOrder.terms || DEFAULT_TERMS_CONDITIONS,
        notes: existingOrder.notes || "",
        fixing: existingOrder.fixing || "0",
        discount: existingOrder.discount || "0",
        transport: existingOrder.transport || existingOrder.freightCharges || "0",
        quotationSignatoryName: existingOrder.quotationSignatoryName || "",
        quotationSignatoryMobile: existingOrder.quotationSignatoryMobile || "",
        kindAttention: existingOrder.kindAttention || "",
        warranty:
          existingOrder.warranty != null && String(existingOrder.warranty).trim() !== ""
            ? String(existingOrder.warranty).trim()
            : DEFAULT_WARRANTY,
        customerRole: typeof cr === "string" ? cr : "",
      });
      setProducts(existingOrder.products || []);
      
    } else {
      setBumpRevision(false);
      // Reset for new order - if dealer, auto-select as customer
      if (role === "dealer") {
        setForm({
          status: "new",
          created_by: userId,
          customerId: userId,
          customerName: username,
          products: [],
          terms: DEFAULT_TERMS_CONDITIONS,
          freightChargesGst: DEFAULT_FREIGHT_CHARGES_GST,
          freightNotes: DEFAULT_FREIGHT_NOTES,
          warranty: DEFAULT_WARRANTY,
        });
      } else {
        setForm({
          status: "new",
          created_by: userId,
          products: [],
          terms: DEFAULT_TERMS_CONDITIONS,
          freightChargesGst: DEFAULT_FREIGHT_CHARGES_GST,
          freightNotes: DEFAULT_FREIGHT_NOTES,
          warranty: DEFAULT_WARRANTY,
        });
      }
      setProducts([]);
    }
  }, [existingOrder, open, role, userId, username]);

  const handleChange = (key: keyof CreateOrderRequest, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Function to convert linear dimensions (MM, CM, Inch, Feet, Meters) to feet
  // Supports all ProductForm UOMs: MM, CM, Inch, Feet, m (Meters)
  // Conversion Table:
  //   1 MM = 0.00328084 feet
  //   1 CM = 0.0328084 feet
  //   1 Inch = 0.0833333 feet
  //   1 Feet = 1 feet
  //   1 Meter = 3.28084 feet
  const convertToFeet = (value: number, uom: string): number => {
    const uomLower = uom?.toLowerCase().trim() || "";
    
    // Mapping of linear UOMs to feet conversion factors
    // Supports all formats from ProductForm: MM, CM, Inch, Feet, m
    const conversions: { [key: string]: number } = {
      // Millimeters
      "mm": 0.00328084,
      "millimeter": 0.00328084,
      "millimeters": 0.00328084,
      
      // Centimeters
      "cm": 0.0328084,
      "centimeter": 0.0328084,
      "centimeters": 0.0328084,
      
      // Inches
      "inch": 0.0833333,
      "in": 0.0833333,
      "inches": 0.0833333,
      
      // Feet
      "ft": 1,
      "feet": 1,
      "foot": 1,
      
      // Meters
      "m": 3.28084,
      "meter": 3.28084,
      "meters": 3.28084,
      "mtr": 3.28084,
      "mtrs": 3.28084,
    };

    const factor = conversions[uomLower];
    
    if (!factor) {
      console.warn(`Unknown UOM: ${uom}. Using default conversion factor of 1.`);
      return value; // Fallback to original value if UOM not found
    }
    
    return value * factor;
  };

  // Function to convert area units to square feet
  // Supports: Sq Ft, Sq M, Sq CM, Sq Inch, Sq Yard
  // Conversion Table:
  //   1 Sq Ft = 1 sq feet
  //   1 Sq M = 10.764 sq feet
  //   1 Sq CM = 0.00107639 sq feet
  //   1 Sq Inch = 0.00694444 sq feet
  //   1 Sq Yard = 9 sq feet
  const convertAreaToSquareFeet = (area: number, uom: string): number => {
    const uomLower = uom?.toLowerCase().trim() || "";
    
    // Mapping of area UOMs to square feet conversion factors
    const conversions: { [key: string]: number } = {
      "sq ft": 1,
      "sqft": 1,
      "square feet": 1,
      "sq.ft": 1,
      "ft²": 1,
      "sq m": 10.764,
      "sqm": 10.764,
      "square meter": 10.764,
      "sq.m": 10.764,
      "m²": 10.764,
      "sq cm": 0.00107639,
      "sqcm": 0.00107639,
      "cm²": 0.00107639,
      "sq inch": 0.00694444,
      "sqin": 0.00694444,
      "in²": 0.00694444,
      "sq yard": 9,
      "sqyd": 9,
      "yd²": 9,
    };

    const factor = conversions[uomLower];
    
    if (!factor) {
      console.warn(`Unknown Area UOM: ${uom}. Using default conversion factor of 1.`);
      return area;
    }
    
    return area * factor;
  };

  const calculateTotals = () => {
    const totalPrice = products.reduce((sum, p) => sum + p.price * p.nos, 0);
    const totalGst = products.reduce((sum, p) => sum + (p.price * p.nos * p.gst) / 100, 0);
    const freightBase =
      parseFloat(String(form.freightCharges ?? "").replace(/,/g, "").trim()) || 0;
    const freightGstPct =
      parseFloat(
        String(form.freightChargesGst ?? DEFAULT_FREIGHT_CHARGES_GST).replace(/,/g, "").trim(),
      ) || 0;
    const freightGstAmt = (freightBase * freightGstPct) / 100;
    const fixing = parseFloat(String(form.fixing ?? "").replace(/,/g, "")) || 0;
    const discount = parseFloat(String(form.discount ?? "").replace(/,/g, "")) || 0;
    const total = totalPrice + totalGst + freightBase + freightGstAmt + fixing - discount;

    handleChange("price", totalPrice.toString());
    handleChange("gst", totalGst.toString());
    handleChange("total", total.toString());
  };

  useEffect(() => {
    calculateTotals();
  }, [products, form.freightCharges, form.freightChargesGst, form.fixing, form.discount]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/customers`, {
        params: { firstResults: 0, maxResults: "all", search: search || "" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

   
  useEffect(() => {
    if (role === "dealer") {
      handleChange("customerId", id);
      handleChange("customerName", username);
    }
  }, [role, id, username]);
  useEffect(() => {
    fetchCustomers();
  }, [search]);

 const handleSubmit = async () => {
  const newErrors: typeof errors = {};

  // Validation checks
  // For dealers, customerId is auto-set, so skip the check
  // For admins/employees, customer must be selected
  if (role !== "dealer" && !form.customerId) {
    newErrors.customer = "Please select a customer";
  }
  if (products.length === 0) {
    newErrors.products = "Please add at least one product";
  }

  setErrors(newErrors);

  // If there are errors, scroll to the first error field
  if (Object.keys(newErrors).length > 0) {
    setTimeout(() => {
      if (newErrors.customer && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      } else if (newErrors.products && scrollContainerRef.current) {
        const productsSection = scrollContainerRef.current.querySelector('[data-products-section]');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
    return;
  }

  const payload: Record<string, unknown> = {
    ...form,
    products,
    customerId: String(form.customerId || id),
    customerName: form.customerName || username,
    createdBy: username,
    quotationCreatedBy: username,
    deliveryDate: form.deliveryDate || null,
    terms: form.terms,
    gst: form.gst || "",
    subject: form.subject || "",
    validity: form.validity || "",
    freightCharges: form.freightCharges || "",
    freightChargesGst: String(
      form.freightChargesGst ?? DEFAULT_FREIGHT_CHARGES_GST,
    ).trim(),
    freightNotes: String(form.freightNotes ?? DEFAULT_FREIGHT_NOTES).trim(),
    fixing: String(form.fixing ?? "0").trim() || "0",
    discount: String(form.discount ?? "0").trim() || "0",
    transport: String(form.freightCharges ?? "0").trim() || "0",
    quotationSignatoryName: String(form.quotationSignatoryName || "").trim(),
    quotationSignatoryMobile: String(form.quotationSignatoryMobile || "").trim(),
    kindAttention: String(form.kindAttention || "").trim(),
    warranty: String(form.warranty ?? DEFAULT_WARRANTY).trim() || DEFAULT_WARRANTY,
    ...(existingOrder
      ? {
          revisedQuotation: bumpRevision,
          invoiceGenerated: Boolean(existingOrder.invoiceGenerated),
          ...(existingOrder.invoiceDate != null && existingOrder.invoiceDate !== ""
            ? { invoiceDate: existingOrder.invoiceDate }
            : {}),
        }
      : {}),
  };

  try {
    if (existingOrder) {
      await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success(
        bumpRevision
          ? "Quotation updated with the next revision number and date."
          : "Quotation updated without changing its revision number or date.",
      );
    } else {
      await axios.post(`${API_BASE_URL}account/${id}/order/create`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Quotation created.");
    }
    refresh();
    onClose();
  } catch (e: unknown) {
    console.error("Error saving order:", e);
    const msg =
      axios.isAxiosError(e) && e.response?.data?.message
        ? String(e.response.data.message)
        : "Could not save quotation. Please try again.";
    toast.error(msg);
  }
};


  return (
    <>
      {/* Drawer Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      )}

      {/* Drawer Container */}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-[65vw] md:h-auto max-h-screen md:max-h-[90vh] rounded-t-2xl md:rounded-lg flex flex-col overflow-hidden ${
          open ? "translate-y-0 md:scale-100 pointer-events-auto opacity-100 visible" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none invisible md:visible"
        }`}
      >
        {/* Header with gradient */}
        <div className="px-6 py-5 border-b-2 border-blue-800/30 shrink-0 flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="text-2xl font-bold text-black drop-shadow-lg">
            {existingOrder ? "📝 Edit Quotation" : "➕ Create Quotation"}
          </h2>
          <button
            onClick={onClose}
            className="text-black hover:text-blue-600 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="space-y-5 px-6 py-5 flex-1 min-h-0 overflow-y-auto pointer-events-auto" ref={scrollContainerRef} data-customer-section>
          <div className="rounded-xl border border-dashed border-blue-300 bg-blue-50 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Quotation Number</p>
            <p className="text-lg font-bold text-blue-900 mt-1">
              {existingOrder?.quotationNumber || (existingOrder ? existingOrder.id : `${quotationSettings.quotationSeries}-${String(quotationSettings.numberSeries).padStart(3, "0")}-R0`)}
            </p>
            <p className="text-xs text-blue-700/80 mt-1">
              {existingOrder
                ? "The revision number and PDF date change only when revision is enabled below."
                : "Preview based on settings. New quotations start at revision R0."}
            </p>
            {existingOrder && (
              <button
                type="button"
                id="advance-quotation-revision"
                role="switch"
                aria-checked={bumpRevision}
                onClick={() => {
                  const enabled = !bumpRevision;
                  setBumpRevision(enabled);
                  setOrderRevisionBumpIntent(existingOrder.id, enabled);
                }}
                className={`mt-3 flex w-full items-center gap-3 rounded-xl border-2 px-3.5 py-3 text-left transition-all ${
                  bumpRevision
                    ? "border-emerald-500 bg-emerald-50 shadow-sm ring-2 ring-emerald-200/60"
                    : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/40"
                }`}
              >
                <span
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                    bumpRevision ? "bg-emerald-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform ${
                      bumpRevision ? "translate-x-6" : "translate-x-1"
                    }`}
                  >
                    {bumpRevision ? (
                      <svg viewBox="0 0 16 16" className="h-3 w-3 text-emerald-600" aria-hidden>
                        <path
                          fill="currentColor"
                          d="M6.5 11.2 3.3 8l1.1-1.1 2.1 2.1 5-5L12.6 5l-6.1 6.2z"
                        />
                      </svg>
                    ) : null}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-bold ${bumpRevision ? "text-emerald-800" : "text-blue-900"}`}>
                    Create next revision
                    {bumpRevision ? (
                      <span className="ml-2 inline-flex items-center rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        On
                      </span>
                    ) : (
                      <span className="ml-2 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-600">
                        Off
                      </span>
                    )}
                  </span>
                  <span className={`mt-0.5 block text-xs ${bumpRevision ? "text-emerald-700/90" : "text-blue-700/80"}`}>
                    {bumpRevision
                      ? `Enabled — each save advances R${existingOrder.revised || 0} → R${(existingOrder.revised || 0) + 1} and updates the PDF date. Turn off manually when done.`
                      : `Enable to advance R${existingOrder.revised || 0} → R${(existingOrder.revised || 0) + 1} and use today's date. You must turn it off manually.`}
                  </span>
                </span>
              </button>
            )}
          </div>

          {/* Error Alert for Customer */}
          {errors.customer && (
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl flex gap-3 animate-shake">
              <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              </div>
              <p className="text-red-700 text-sm font-bold">{errors.customer}</p>
            </div>
          )}

          {/* Customer dropdown */}

          <div className={`bg-white p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all ${
            errors.customer ? "border-red-400 bg-red-50" : "border-blue-200/50"
          }`}>
            <Label className="font-bold text-gray-800 mb-2 block">👤 Customer</Label>
            {role === "dealer" ? (
              // For dealers, show a non-editable display
              <div className="w-full p-3 h-12 rounded-lg border-2 border-blue-300 bg-blue-50 font-semibold text-gray-700 flex items-center">
                {form.customerName || "Loading..."}
              </div>
            ) : (
              // For admins/employees, show editable dropdown
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between border-2 border-blue-300 hover:border-blue-500 h-12 rounded-lg font-semibold text-gray-700">
                    {form.customerName || "Select Customer"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="bottom"
                  sideOffset={8}
                  collisionPadding={16}
                  className="z-[250] p-0 flex flex-col bg-white shadow-xl rounded-xl border-2 border-blue-200 w-[min(calc(100vw-1.5rem),22rem)] sm:w-[min(calc(100vw-2rem),26rem)] md:w-[min(28rem,calc(100vw-2rem))] max-w-[calc(100vw-1.5rem)]"
                >
                  <div className="p-3 sm:p-4 border-b-2 border-blue-100 shrink-0">
                    <Input
                      placeholder="🔍 Search name or mobile..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="border-2 border-blue-300 rounded-lg h-10 sm:h-11 font-medium"
                    />
                  </div>

                <div className="h-52 sm:h-60 overflow-y-auto overscroll-contain space-y-1 p-2 sm:p-3">
                  {customers
                    .filter(
                      (c) => !search ||
                        c.companyName.toLowerCase().includes(search.toLowerCase()) ||
                        c.personName.toLowerCase().includes(search.toLowerCase()) ||
                        c.personMobile.includes(search)
                    )
                    .map((c) => (
                      <div
                        key={c.id}
                        className={cn(
                          "p-3 rounded-lg cursor-pointer hover:bg-blue-100 transition-all border-l-4 border-transparent",
                          form.customerId === String(c.id) && "bg-blue-50 border-l-4 border-blue-600 shadow-sm"
                        )}
                        onClick={() => {
                          handleChange("customerId", String(c.id));
                          handleChange("customerName", c.companyName);
                          handleChange("customerRole", "");
                          setPopoverOpen(false);
                        }}
                      >
                        <div className="font-bold text-gray-900 text-sm sm:text-base break-words">{c.companyName}</div>
                        <div className="text-sm text-gray-600 break-words">{c.personName} · {c.personMobile}</div>
                        {c.gst && <div className="text-xs text-blue-600 font-semibold mt-1">GST: {c.gst}</div>}
                      </div>
                    ))}
                  {customers.filter(
                    (c) =>
                      !search ||
                      c.companyName.toLowerCase().includes(search.toLowerCase()) ||
                      c.personName.toLowerCase().includes(search.toLowerCase()) ||
                      c.personMobile.includes(search),
                  ).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-6">No customers found</p>
                  )}
                </div>

                <div className="shrink-0 border-t-2 border-blue-100 p-3 sm:p-4 flex justify-end bg-blue-50">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPopoverOpen(false);
                      setCustomerModalOpen(true);
                    }}
                    className="bg-blue-600 text-white border-0 hover:bg-blue-700 font-bold"
                  >
                    + Add Customer
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            )}
          </div>



          {/* Products */}
          {/* 🧾 Products Table Section */}
          {errors.products && (
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl flex gap-3 animate-shake">
              <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              </div>
              <p className="text-red-700 text-sm font-bold">{errors.products}</p>
            </div>
          )}

          <div className={`border-2 rounded-xl p-5 shadow-sm bg-gradient-to-br from-green-50 via-white to-cyan-50 ${
            errors.products ? "border-red-400" : "border-green-200/50"
          }`} data-products-section>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-xl text-gray-800 flex items-center gap-2">🛍️ Products</h3>
              <Button 
                onClick={() => setProductModalOpen(true)}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold hover:shadow-lg hover:shadow-green-500/40 transition-all"
              >
                + Add Product
              </Button>
            </div>

            {products.length === 0 ? (
              <p className="text-gray-500 text-center py-8 font-semibold">No products added yet. Click the button above to add products.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-green-200">
                <table className="w-full min-w-[720px] text-sm table-fixed">
                  <thead>
                    <tr className="bg-gradient-to-r from-green-100 to-emerald-100 border-b-2 border-green-300">
                      <th className="w-12 px-3 py-3 text-left font-bold text-gray-800 whitespace-nowrap">#</th>
                      <th className="px-3 py-3 text-left font-bold text-gray-800">Product Name</th>
                      {role != "dealer" && <>
                      <th className="w-16 px-3 py-3 text-center font-bold text-gray-800 whitespace-nowrap">Qty</th>
                      <th className="w-28 px-3 py-3 text-right font-bold text-gray-800 whitespace-nowrap">Price/Unit</th>
                      <th className="w-28 px-3 py-3 text-right font-bold text-gray-800 whitespace-nowrap">Amount</th>
                      </>}
                      <th className="w-56 px-3 py-3 text-center font-bold text-gray-800 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, i) => (
                      (() => {
                        const quantity = parseFloat(String(p.nos ?? 0).replace(/,/g, "")) || 0;
                        const unitPrice = parseFloat(String(p.price ?? 0).replace(/,/g, "")) || 0;
                        const amountBeforeGst = quantity * unitPrice;
                        return (
                      <tr
                        key={i}
                        className="border-b border-green-100 hover:bg-green-50/80 transition-all"
                      >
                        <td className="px-3 py-3 font-bold text-gray-700 whitespace-nowrap">{i + 1}</td>
                        <td className="px-3 py-3">
                          <div className="font-bold text-gray-900 break-words">{p.name}</div>
                          {p.description && (
                            <div className="text-xs text-gray-500 mt-1 italic break-words">{p.description}</div>
                          )}

                        </td>
                       {role != "dealer" && <>
                       <td className="px-3 py-3 text-center text-gray-700 whitespace-nowrap">{p.nos}</td>
                        <td className="px-3 py-3 text-right font-bold text-blue-600 whitespace-nowrap">₹{unitPrice.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right font-bold text-emerald-600 whitespace-nowrap">₹{amountBeforeGst.toFixed(2)}</td>
                       </> }
                        <td className="px-3 py-3">
                          <div className="flex flex-nowrap items-center justify-center gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingProduct(p);
                              setProductModalOpen(true);
                            }}
                            className="h-8 shrink-0 whitespace-nowrap border border-blue-400 px-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              const clone = { ...p };
                              setProducts([...products.slice(0, i + 1), clone, ...products.slice(i + 1)]);
                            }}
                            className="h-8 shrink-0 whitespace-nowrap bg-cyan-500 px-2.5 text-xs font-semibold text-white hover:bg-cyan-600"
                          >
                            Duplicate
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              setProducts(products.filter((_, idx) => idx !== i))
                            }
                            className="h-8 shrink-0 whitespace-nowrap bg-red-500 px-2.5 text-xs font-semibold text-white hover:bg-red-600"
                          >
                            Delete
                          </Button>
                          </div>
                        </td>
                      </tr>
                        );
                      })()
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Freight (before price summary) */}
          {role != "dealer" && (
            <div className="bg-white border-2 border-slate-200 rounded-xl p-5 shadow-sm mb-4">
              <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">🚚 Freight charges</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold text-gray-800 mb-2 block">Amount (₹)</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.freightCharges || ""}
                    onChange={(e) => handleChange("freightCharges", e.target.value)}
                    placeholder="e.g. 500"
                    className="border-2 border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="font-bold text-gray-800 mb-2 block">GST % on freight</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.freightChargesGst ?? DEFAULT_FREIGHT_CHARGES_GST}
                    onChange={(e) => handleChange("freightChargesGst", e.target.value)}
                    placeholder="Default 18% (change or set 0 for no GST)"
                    className="border-2 border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Price summary */}
         { role != "dealer" && <div className="bg-gradient-to-br from-orange-50 via-white to-amber-50 border-2 border-orange-200/50 rounded-xl p-5 shadow-sm">
            <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">💰 Price Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white border-2 border-orange-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Total (₹)</Label>
              <Input
                placeholder="0.00"
                value={form.price ? parseFloat(form.price.toString()).toFixed(2) : ""}
                onChange={(e) => handleChange("price", e.target.value)}
                className="border-0 text-lg font-bold text-orange-600 focus:ring-0 p-0"
                readOnly
              />
            </div>
            <div className="bg-white border-2 border-blue-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">GST on products (₹)</Label>
              <Input
                placeholder="0.00"
                value={form.gst ? parseFloat(form.gst.toString()).toFixed(2) : ""}
                onChange={(e) => handleChange("gst", e.target.value)}
                className="border-0 text-lg font-bold text-blue-600 focus:ring-0 p-0"
                readOnly
              />
            </div>
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg p-3 text-white">
              <Label className="text-xs font-bold text-cyan-100 block mb-1">GRAND TOTAL (₹)</Label>
              <div className="text-lg font-bold break-words">₹{form.total ? parseFloat(form.total.toString()).toFixed(2) : "0.00"}</div>
            </div>
          </div>
          {(() => {
            const fb = parseFloat(String(form.freightCharges || "").replace(/,/g, "").trim()) || 0;
            const fp = parseFloat(String(form.freightChargesGst || "").replace(/,/g, "").trim()) || 0;
            const fa = (fb * fp) / 100;
            if (fb <= 0) return null;
            return (
              <div className="mt-3 pt-3 border-t border-orange-200/60 text-sm text-gray-700 space-y-1">
                <p className="flex justify-between font-medium text-gray-900">
                  <span>{formatFreightChargesLineLabel(fb, fp)}</span>
                  <span>₹{(fp > 0 ? fb + fa : fb).toFixed(2)}</span>
                </p>
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
           
            <div className="bg-white border-2 border-rose-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Discount (₹)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.discount ?? ""}
                onChange={(e) => handleChange("discount", e.target.value)}
                placeholder="0.00"
                className="border-0 text-lg font-bold text-rose-700 focus:ring-0 p-0"
              />
            </div>
          </div>
          </div>}

          {/* Subject / Delivery / Validity */}
          {role != "dealer" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl border-2 border-blue-200 shadow-sm md:col-span-2">
                <Label className="font-bold text-gray-800 mb-2 block">👤 Kind Attention</Label>
                <Input
                  value={form.kindAttention || ""}
                  onChange={(e) => handleChange("kindAttention", e.target.value)}
                  placeholder="e.g. Mr. Kumar / 9876543210"
                  className="border-2 border-blue-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Shown after “Kind Attn:” in the quotation PDF. If left blank, the customer contact is used.
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                <Label className="font-bold text-gray-800 mb-2 block">📋 Subject</Label>
                <Input
                  value={form.subject || ""}
                  onChange={(e) => handleChange("subject", e.target.value)}
                  placeholder="Enter subject..."
                  className="border-2 border-gray-300 rounded-lg"
                />
              </div>
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                <Label className="font-bold text-gray-800 mb-2 block">🚚 Delivery</Label>
                <Input
                  value={form.deliveryDate ? String(form.deliveryDate) : ""}
                  onChange={(e) => handleChange("deliveryDate", e.target.value)}
                  placeholder="e.g. 7-10 working days"
                  className="border-2 border-gray-300 rounded-lg"
                />
              </div>
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm md:col-span-2">
                <Label className="font-bold text-gray-800 mb-2 block">📅 Validity</Label>
                <Input
                  value={form.validity || ""}
                  onChange={(e) => handleChange("validity", e.target.value)}
                  placeholder="e.g. 30 days"
                  className="border-2 border-gray-300 rounded-lg"
                />
              </div>
            </div>
          )}

          {/* PDF signatory (optional; shown on quotation after Grand Total) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SignatoryPicker
              label="✍️ Signatory name (PDF)"
              placeholder="Search or select a signatory"
              searchPlaceholder="Search by name or mobile..."
              helperText="Selecting a name fills its mobile number automatically."
              field="name"
              value={form.quotationSignatoryName || ""}
              signatories={signatories}
              onSelect={(signatory) => {
                handleChange("quotationSignatoryName", signatory.name);
                handleChange("quotationSignatoryMobile", signatory.mobile);
              }}
              onCustomValue={(value) => handleChange("quotationSignatoryName", value)}
              onClear={() => handleChange("quotationSignatoryName", "")}
            />
            <SignatoryPicker
              label="📱 Signatory mobile (PDF)"
              placeholder="Search or select a mobile number"
              searchPlaceholder="Search by mobile or name..."
              helperText="Selecting a mobile number fills its name automatically."
              field="mobile"
              value={form.quotationSignatoryMobile || ""}
              signatories={signatories}
              onSelect={(signatory) => {
                handleChange("quotationSignatoryMobile", signatory.mobile);
                handleChange("quotationSignatoryName", signatory.name);
              }}
              onCustomValue={(value) => handleChange("quotationSignatoryMobile", value)}
              onClear={() => handleChange("quotationSignatoryMobile", "")}
            />
          </div>

          {/* Freight notes (Terms & Conditions — section 3 on PDF) */}
          {role != "dealer" && (
            <div className="bg-white p-4 rounded-xl border-2 border-indigo-200/50 shadow-sm hover:shadow-md transition-all">
              <Label className="font-bold text-gray-800 mb-3 block">🚚 Freight Notes</Label>
              <Textarea
                value={form.freightNotes ?? DEFAULT_FREIGHT_NOTES}
                onChange={(e) => handleChange("freightNotes", e.target.value)}
                placeholder="Default: EXTRA — shown under Terms & Conditions, section 3. Freight Charges on the PDF"
                className="border-2 border-indigo-300 rounded-lg min-h-32 focus:border-indigo-500 font-medium text-gray-700"
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 This text appears on the quotation PDF in the Freight Charges clause (default: EXTRA).
              </p>
            </div>
          )}

          {/* Warranty (Terms & Conditions — section 8 on PDF) */}
          {role != "dealer" && (
            <div className="bg-white p-4 rounded-xl border-2 border-amber-200/60 shadow-sm hover:shadow-md transition-all">
              <Label className="font-bold text-gray-800 mb-3 block">🛡️ Warranty</Label>
              <Textarea
                value={form.warranty ?? DEFAULT_WARRANTY}
                onChange={(e) => handleChange("warranty", e.target.value)}
                placeholder={DEFAULT_WARRANTY}
                className="border-2 border-amber-300 rounded-lg min-h-24 focus:border-amber-500 font-medium text-gray-700"
              />
              <p className="text-xs text-gray-500 mt-2">
                Shown on the quotation PDF under Terms &amp; Conditions, section 8. Warranty.
              </p>
            </div>
          )}

          {/* Terms and Conditions */}
          { role != "dealer" && (
            <div className="bg-white p-4 rounded-xl border-2 border-indigo-200/50 shadow-sm hover:shadow-md transition-all">
              <Label className="font-bold text-gray-800 mb-3 block">📋 Payment Terms</Label>
              <Textarea
                value={form.terms || ""}
                onChange={(e) => handleChange("terms", e.target.value)}
                placeholder="Enter terms and conditions..."
                className="border-2 border-indigo-300 rounded-lg min-h-32 focus:border-indigo-500 font-medium text-gray-700"
              />
              <p className="text-xs text-gray-500 mt-2">💡 Tip: Use bullet points (•) to format conditions</p>
            </div>
          )}

          {/* Internal Notes */}
          <div className="bg-white p-4 rounded-xl border-2 border-amber-200/50 shadow-sm hover:shadow-md transition-all">
            <Label className="font-bold text-gray-800 mb-3 block">📝 Internal Notes</Label>
            <Textarea
              value={form.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Add internal notes about this order..."
              className="border-2 border-amber-300 rounded-lg min-h-24 focus:border-amber-500 font-medium text-gray-700"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-gray-50 rounded-b-2xl md:rounded-b-lg flex-shrink-0">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-2 border-gray-400 hover:bg-gray-100 font-bold text-gray-700 h-11 rounded-lg"
          >
            ✕ Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold hover:shadow-lg hover:shadow-blue-500/40 transition-all h-11 rounded-lg px-6"
          >
            ✓ Save Order
          </Button>
        </div>
      </div>

       <ProductForm
  open={productModalOpen}
  onClose={() => {
    setProductModalOpen(false);
    setEditingProduct(null);
  }}
  onAdd={(product: ProductItem[]) => {
    if (editingProduct) {
      setProducts((prev) =>
        prev.map((p) =>
          p === editingProduct ? { ...product[0] } : p
        )
      );
    } else {
      setProducts((prev) => [...prev, ...product]);
    }
    setEditingProduct(null);
    setProductModalOpen(false);
  }}
  existingProduct={editingProduct as any}
  customerRole={form.customerRole || ""}
  userRole={userRole}
  otherModalOpen={customerModalOpen}
/>

       <CustomerForm
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onAdd={(customer) => {
          setCustomers((prev) => [...prev, customer]);
          setCustomerModalOpen(false);
          handleChange("customerId", String(customer.id));
          handleChange("customerName", customer.companyName);
          handleChange("customerRole", "");
        }}
      />
    </>
  );
};
